import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Creators as ProductCreators } from 'store/products/products-duck.js';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ImageCarousel from 'components/image-carousel';
import * as S from './styled.js';
import { getWhatsappUrl } from 'utils/whatsappLinkGenerator.js';
import { Helmet } from 'react-helmet';

const ProductDetailsPage = ({ productDetails, productFetchByIdAsync }) => {
  const { id } = useParams();

  useEffect(() => {
    if (id) productFetchByIdAsync(id);
  }, [id, productFetchByIdAsync]);

  if (!productDetails) return null;

  const { name, price, promotionalPrice, tags, description, technicalSpecifications, images } =
    productDetails;

  const formattedPrice = (value) =>
    value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <>
      <Helmet>
        <title>{name} | HiCell</title>
        <meta property="og:title" content={name} />
        <meta
          property="og:description"
          content="Confira este produto incrível disponível na HiCell!"
        />
        <meta property="og:image" content={images?.[0]} />
        <meta property="og:url" content={`${window.location.origin}/products/${id}`} />
        <meta property="og:type" content="product" />
      </Helmet>

      <S.Container>
        <S.ContentWrapper container spacing={4}>
          <Grid item size={{ xs: 12, md: 5 }}>
            <ImageCarousel images={images || []} />
          </Grid>

          <Grid item size={{ xs: 12, md: 7 }} color={'#fff'}>
            <Typography variant="h4" fontWeight="bold" mb={1}>
              {name}
            </Typography>

            <S.PriceArea>
              {promotionalPrice ? (
                <>
                  <Typography variant="body2" sx={{ textDecoration: 'line-through', mr: 2 }}>
                    {formattedPrice(price)}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formattedPrice(promotionalPrice)}
                  </Typography>
                </>
              ) : (
                <Typography variant="h5" fontWeight="bold">
                  {formattedPrice(price)}
                </Typography>
              )}
            </S.PriceArea>

            <S.TagList>
              {tags?.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.description}
                  sx={{ bgcolor: '#92b21f', color: '#fff' }}
                />
              ))}
            </S.TagList>

            <Button
              variant="contained"
              size="large"
              startIcon={<WhatsAppIcon />}
              sx={{
                mt: 3,
                bgcolor: '#92b21f',
                color: 'white',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#7aa01a' },
              }}
              href={getWhatsappUrl(id)}
              target="_blank"
            >
              NEGOCIE COM UM DE NOSSOS VENDEDORES!
            </Button>
          </Grid>
        </S.ContentWrapper>

        <Box mt={6}>
          {(description && (
            <Accordion defaultExpanded sx={{ bgcolor: '#333', color: '#92b21f' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Descrição</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ color: '#fff' }}>
                <div dangerouslySetInnerHTML={{ __html: description }} />
              </AccordionDetails>
            </Accordion>
          ))}

          {technicalSpecifications && (
            <Accordion sx={{ bgcolor: '#333', color: '#92b21f' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Ficha Técnica</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ color: '#fff' }}>
                <div dangerouslySetInnerHTML={{ __html: technicalSpecifications }} />
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </S.Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  productDetails: state.Product.productToUpdate,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(ProductCreators, dispatch);

ProductDetailsPage.propTypes = {
  productDetails: PropTypes.object,
  productFetchByIdAsync: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPage);
